import * as React from 'react';
import * as Sentry from '@sentry/react';
import {
  CreateBase,
  useNotify,
  useRefresh,
  useDataProvider,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton as MuiIconButton,
  Box,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField as MuiTextField, MenuItem } from '@mui/material';
import { CommissionService } from "../../services/commission/commissionService.ts";

import { COMMISSIONS } from '../../enums/Commissions.ts';
import { useLocation, useNavigate } from "react-router-dom";

type Role = keyof typeof COMMISSIONS;

const COMMISSION_CHOICES = (Object.keys(COMMISSIONS) as Role[]).map((id) => ({
  id,
  name: COMMISSIONS[id].label,
}));

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
    '& .MuiSelect-outlined': {
      borderRadius: '6px',
      height: '1.4375em',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
    },
  },
  '& .MuiInputBase-input': { fontSize: 14, padding: '10px 12px' },
};

type Props = { open: boolean; onClose: () => void };

export const CommissionCreateDialog: React.FC<Props> = ({ open, onClose }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [items, setItems] = React.useState<
    { id: string; label: string; amount: number | string; unitType: string }[]
  >([]);

  const location = useLocation();
  const navigate = useNavigate();
  const isFirstCreate = location.pathname.endsWith("/create");
  !open ? open = isFirstCreate : open = open;

  const initialValues = {
    name: '',
    role: '',
    amount: '',
    unitType: '',
  };

  const validationSchema = (items: Array<{ id: string }>) =>
    Yup.object({
      name: Yup.string().required('Required'),
      role: Yup.string().required('Required')
        .test('unique', 'This commission has already been added', (role) => !items.some(it => it.id === role)),
      amount: Yup.number().typeError('Must be a number').required('Required'),
      unitType: Yup.string().required('Required'),
    });

  const { t } = useTranslation();
  const commissionService = new CommissionService();

  const handleClose = () => {
    navigate("/commissionManagement");
  };

  return (
    <CreateBase resource="commissionManagement">
      <Dialog
        open={open}
        onClose={!isFirstCreate ? onClose : handleClose}
        fullWidth
        maxWidth="lg"
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
          paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
        }}
        sx={{ padding: 0 }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('commissionManagement.addNewAgency')}
          <MuiIconButton aria-label="close" onClick={!isFirstCreate ? onClose : handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </MuiIconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema(items)}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
              const role = values.role as Role;
              setItems(prev => [...prev, { id: role, label: COMMISSIONS[role].label, amount: values.amount, unitType: values.unitType }]);
              notify('Item added', { type: 'success' });
              resetForm({ values: { ...values, role: '', amount: '', unitType: '' } });
              setSubmitting(false);
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%', gap: 3 }}>
                    <MuiTextField
                      name="name"
                      label=""
                      placeholder={t('commissionManagement.dialog.nameOfAgency')}
                      sx={inputSx}
                      size="small"
                      fullWidth
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        borderColor: 'var(--color-secondary)',
                        gap: 3,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        padding: 3,
                        borderRadius: 'var(--radius-6)',
                      }}
                    >
                      <MuiTextField
                        select
                        name="role"
                        label=""
                        sx={inputSx}
                        size="small"
                        fullWidth
                        value={values.role}
                        onChange={(e) => {
                          const role = e.target.value as Role;
                          setFieldValue('role', role);
                          setFieldValue('unitType', role ? COMMISSIONS[role].unit : '');
                        }}
                        onBlur={handleBlur}
                        error={touched.role && Boolean(errors.role)}
                        helperText={touched.role && errors.role}
                      >
                        {COMMISSION_CHOICES.map(c => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </MuiTextField>

                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                        <MuiTextField
                          name="amount"
                          label=""
                          placeholder={t('commissionManagement.dialog.amount')}
                          sx={inputSx}
                          size="small"
                          fullWidth
                          value={values.amount}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.amount && Boolean(errors.amount)}
                          helperText={touched.amount && errors.amount}
                        />

                        <MuiTextField
                          // select
                          name="unitType"
                          label=""
                          sx={inputSx}
                          size="small"
                          fullWidth
                          value={values.unitType}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled
                          error={touched.role && Boolean(errors.role)}
                          helperText={touched.role && errors.role}
                        >
                          {/* {COMMISSION_UNIT_TYPES.map(c => (
                            <MenuItem key={c.id} value={c.name}>
                              {c.name}
                            </MenuItem>
                          ))} */}
                        </MuiTextField>
                      </Box>

                      <Button
                        type="submit"
                        variant="contained"
                        sx={{
                          width: 'fit-content',
                          px: 4,
                          borderRadius: 0,
                          backgroundColor: '#18C5B3',
                          color: 'white',
                          boxShadow: 'none',
                          alignSelf: 'flex-end',
                          '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                        }}
                      >
                        {t('common.add')}
                      </Button>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '50%',
                      borderColor: 'var(--color-secondary)',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      padding: 3,
                      borderRadius: 'var(--radius-6)',
                      gap: 1.5,
                    }}
                  >
                    {items.map((it, idx) => (
                      <Box
                        key={`${it.id}-${idx}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          border: '1px solid var(--color-secondary)',
                          borderRadius: '6px',
                          px: 2,
                          py: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>{it.label}</Box>
                        <Box sx={{ width: 80, textAlign: 'right' }}>{it.amount}</Box>
                        <Box sx={{ width: 80, textAlign: 'right' }}>{it.unitType}</Box>
                        <Button
                          onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))}
                          variant="text"
                          sx={{ minWidth: 0 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={!isFirstCreate ? onClose : handleClose}
                    sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                  >
                    {t('common.cancel')}
                  </Button>

                  <Button
                    type="button"
                    onClick={async () => {
                      try {
                        const payload = {
                          agencyName: values.name.trim(),
                          agencyCommissionList: items.map(it => ({
                            amount: Number(it.amount),
                            commissionType: it.label
                          })),
                        };
                        await commissionService.createAgency(payload);

                        notify(t('common.saved'), { type: 'success' });
                        setItems([]);
                        refresh();
                        isFirstCreate ? handleClose() : onClose();
                      } catch (error: any) {
                        notify(error.response.data.error, { type: 'error' });
                        console.error(error.response.data.error);
                        Sentry.captureException(error);
                      }
                    }}
                    disabled={items.length === 0}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-text-light)',
                      boxShadow: 'none',
                      '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                      '&:disabled': { cursor: 'default', backgroundColor: 'var(--color-button-background-disabled)', color: 'var(--color-button-text-disabled)' }
                    }}
                  >
                    {t('common.saveChanges')}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </CreateBase>
  );
};
